import { z } from 'zod';

/**
 * Regras do "Criar conta" — espelho de `identity/domain/registration.py` na
 * API, que é quem decide de verdade (e as mesmas do Keycloak, aplicadas pelo
 * configure-realm.sh). Aqui elas servem para a pessoa ver o que falta antes
 * de enviar.
 */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;
export const NAME_MAX = 120;

/** Chaves iguais às que a API devolve em `error.rules` (`weak_password`). */
export type PasswordRule = 'too_short' | 'too_long' | 'no_letter' | 'no_digit' | 'equals_email' | 'policy';

/** As regras que a tela lista, na ordem em que aparecem. */
export const PASSWORD_RULES: { rule: PasswordRule; label: string }[] = [
  { rule: 'too_short', label: `Pelo menos ${PASSWORD_MIN} caracteres` },
  { rule: 'no_letter', label: 'Pelo menos uma letra' },
  { rule: 'no_digit', label: 'Pelo menos um número' },
  { rule: 'equals_email', label: 'Diferente do seu e-mail' },
];

export function passwordViolations(password: string, email: string): PasswordRule[] {
  const broken: PasswordRule[] = [];
  if (password.length < PASSWORD_MIN) broken.push('too_short');
  if (password.length > PASSWORD_MAX) broken.push('too_long');
  if (!/\p{L}/u.test(password)) broken.push('no_letter');
  if (!/\p{Nd}/u.test(password)) broken.push('no_digit');
  if (password.trim().toLowerCase() === email.trim().toLowerCase()) broken.push('equals_email');
  return broken;
}

// O mesmo validador de nome do Keycloak (person-name-prohibited-characters).
// eslint-disable-next-line no-control-regex
const PROHIBITED_NAME_CHARS = /[<>&"\v$%!#?§;*~/\\|^=[\]{}()\u0000-\u001f\u007f]/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Por que o nome não serve — ou `undefined`, se serve. */
export function nameProblem(raw: string): string | undefined {
  const words = raw.trim().split(/\s+/).filter(Boolean);
  const full = words.join(' ');
  if (!full) return 'Informe seu nome';
  if (full.length > NAME_MAX) return `Use no máximo ${NAME_MAX} caracteres`;
  if (PROHIBITED_NAME_CHARS.test(full)) return 'O nome não pode ter símbolos como / ( ) # $';
  if (!/\p{L}/u.test(full)) return 'Informe seu nome com letras';
  // Sem sobrenome o Keycloak não deixa entrar por senha ("conta não configurada").
  if (words.length < 2) return 'Informe nome e sobrenome';
  return undefined;
}

export const signupSchema = z
  .object({
    name: z.string().superRefine((value, ctx) => {
      const problem = nameProblem(value);
      if (problem) ctx.addIssue({ code: 'custom', message: problem });
    }),
    email: z.string().trim().min(1, 'Informe seu e-mail').regex(EMAIL, 'Confira o e-mail'),
    password: z.string().min(1, 'Crie uma senha'),
  })
  .superRefine(({ password, email }, ctx) => {
    if (password && passwordViolations(password, email).length > 0)
      ctx.addIssue({
        code: 'custom',
        path: ['password'],
        message: 'A senha ainda não atende aos requisitos',
      });
  });

export type SignupValues = z.infer<typeof signupSchema>;
